import { useState, useEffect, useCallback, forwardRef } from 'react';
import useDarkMode from '../../../../hooks/useDarkMode';

interface IBudgetCurrencyInputProps {
  value: number | string;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  isRemoved?: boolean;
  isChanged?: boolean;
}

const formatCurrencyDisplay = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  
  if (!numericValue) {
    return '';
  }

  const cents = parseInt(numericValue, 10);
  const reais = Math.floor(cents / 100);
  const centavos = cents % 100;

  const formattedReais = reais.toLocaleString('pt-BR');
  const formattedCentavos = String(centavos).padStart(2, '0');

  return `R$ ${formattedReais},${formattedCentavos}`;
};

const parseFromNumericValue = (value: string | number): number => {
  if (typeof value === 'number') {
    return value;
  }
  
  if (!value) {
    return 0;
  }

  const cleanValue = value.toString().replace(/[^\d.]/g, '');
  const parts = cleanValue.split('.');
  
  if (parts.length === 1) {
    return Number.parseFloat(parts[0] || '0');
  }

  const reais = Number.parseFloat(parts[0] || '0');
  const centavos = Number.parseFloat((parts[1] || '00').padEnd(2, '0').substring(0, 2)) / 100;

  return reais + centavos;
};

const BudgetCurrencyInput = forwardRef<HTMLInputElement, IBudgetCurrencyInputProps>((props, ref) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [rawValue, setRawValue] = useState<number>(0);
  const isDarkMode = useDarkMode();

  useEffect(() => {
    if (props.value !== undefined && props.value !== null && props.value !== '' && props.value !== 0) {
      const numericValue = typeof props.value === 'number' ? props.value : parseFromNumericValue(props.value.toString());
      const cents = Math.round(numericValue * 100);
      setRawValue(cents);
      setDisplayValue(formatCurrencyDisplay(String(cents)));
    } else {
      setRawValue(0);
      setDisplayValue('');
    }
  }, [props.value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const numericValue = inputValue.replace(/\D/g, '');

      if (!numericValue) {
        setRawValue(0);
        setDisplayValue('');
        props.onChange(0);
        return;
      }

      const cents = parseInt(numericValue, 10);
      const numericValueConverted = cents / 100;
      setRawValue(cents);
      const formatted = formatCurrencyDisplay(String(cents));
      setDisplayValue(formatted);
      props.onChange(numericValueConverted);
    },
    [props]
  );

  const handleBlur = useCallback(() => {
    if (rawValue === 0) {
      setDisplayValue('');
      props.onChange(0);
    } else {
      const numericValue = rawValue / 100;
      setDisplayValue(formatCurrencyDisplay(String(rawValue)));
      props.onChange(numericValue);
    }
  }, [rawValue, props]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  const getBackgroundColor = () => {
    if (props.isRemoved) return isDarkMode ? '#3a1f20' : '#fef2f2';
    if (props.isChanged) return isDarkMode ? '#173327' : '#d1fae5';
    return undefined;
  };

  return (
    <input
      ref={ref}
      type="text"
      className={`form-control text-right ${props.className || ''}`}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={props.placeholder || 'R$ 00,00'}
      disabled={props.disabled}
      style={{ backgroundColor: getBackgroundColor() }}
    />
  );
});

BudgetCurrencyInput.displayName = 'BudgetCurrencyInput';

export default BudgetCurrencyInput;

